import {Component, OnInit} from '@angular/core';
import {CommonModule} from '@angular/common';
import {FormBuilder, FormGroup, ReactiveFormsModule, Validators,} from '@angular/forms';
import {ActivatedRoute, Router} from '@angular/router';
import {Store} from '@ngrx/store';
import {combineLatest, map, Observable} from 'rxjs';
import {NzTabsModule} from 'ng-zorro-antd/tabs';
import {NzInputModule} from 'ng-zorro-antd/input';
import {NzButtonModule} from 'ng-zorro-antd/button';
import {NzAvatarModule} from 'ng-zorro-antd/avatar';
import {NzIconModule} from 'ng-zorro-antd/icon';
import {NzSwitchModule} from 'ng-zorro-antd/switch';
import {NzTableModule} from 'ng-zorro-antd/table';
import {NzDatePickerModule} from 'ng-zorro-antd/date-picker';
import {
  MbButtonComponent,
  MbImageUploadComponent,
  MbInputComponent,
  MbSuccessModalComponent,
  MbSwitchComponent,
  ToastService,
} from '../../../components/common';
import {ASSET_CONSTANTS} from '../../../shared/constants/asset-constants';
import {AppState} from '../../../store/app.state';
import {selectAuthUser} from '../../../store/auth/auth.selectors';
import {User} from '../../../store/auth/auth.state';
import {ProfileService, UserService} from '../../../core/services';
import {ProfileSchema} from './profile.schema';
import {phoneValidator} from '../../../utils/phone-validator';
import {TranslocoDirective} from '@jsverse/transloco';

interface OriginalFormValues {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  profilePicture: string;
}

@Component({
  selector: 'app-profile',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    NzTabsModule,
    NzInputModule,
    NzButtonModule,
    NzAvatarModule,
    NzIconModule,
    NzSwitchModule,
    NzTableModule,
    NzDatePickerModule,
    MbInputComponent,
    MbImageUploadComponent,
    MbSwitchComponent,
    MbButtonComponent,
    MbSuccessModalComponent,
    TranslocoDirective,
  ],
  templateUrl: './profile.component.html',
  styleUrls: ['./profile.component.css'],
})
export class ProfileComponent implements OnInit {
  protected readonly ASSET_CONSTANTS = ASSET_CONSTANTS;
  isSuccessModalVisible = false;
  successMessage = 'Profile updated successfully';
  selectedTabIndex = 0;
  profileForm: FormGroup;
  apiKeyForm: FormGroup;
  zodErrors: Record<string, string> = {};
  user$: Observable<User | null>;
  hasChanges$: Observable<boolean>;

  private originalFormValues: OriginalFormValues | null = null;
  profilePicturePreview: string | null = null;

  notificationPrefs = {
    WHATSAPP: true,
    SMS: true,
    EMAIL: true,
  };

  isLoading = false;
  isPasswordLoading = false;

  constructor(
    private route: ActivatedRoute,
    private fb: FormBuilder,
    private store: Store<AppState>,
    private profileService: ProfileService,
    private userService: UserService,
    private toastService: ToastService,
    private router: Router
  ) {
    this.user$ = this.store.select(selectAuthUser);

    this.profileForm = this.fb.group({
      firstName: [{ value: '', disabled: true }, Validators.required],
      lastName: [{ value: '', disabled: true }, Validators.required],
      email: [{ value: '', disabled: true }, Validators.required],
      phone: [
        { value: '', disabled: true },
        [Validators.required, phoneValidator],
      ],
      oldPassword: [''],
      newPassword: [''],
      confirmPassword: [''],
      profilePicture: [null as File | null],
    });

    this.apiKeyForm = this.fb.group({
      name: ['', Validators.required],
      key: ['', Validators.required],
    });

    this.hasChanges$ = combineLatest([
      this.profileForm.valueChanges,
      this.user$,
    ]).pipe(
      map(([formValues, user]) => {
        if (!user || !this.originalFormValues) return false;

        const personalInfoChanged =
          formValues.firstName !== this.originalFormValues.firstName ||
          formValues.lastName !== this.originalFormValues.lastName ||
          formValues.phone !== this.originalFormValues.phone;

        const profilePictureChanged = !!formValues.profilePicture;

        return personalInfoChanged || profilePictureChanged;
      })
    );
  }

  ngOnInit(): void {
    this.route.fragment.subscribe((fragment) => {
      if (fragment === 'system-config') {
        this.selectedTabIndex = 1;
      }
    });

    this.user$.subscribe((user) => {
      if (user) {
        const formValues: OriginalFormValues = {
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          phone: user.phoneNumber.replace(/^0/, '+250'),
          profilePicture: user.profilePictureUrl || "",
        };
        this.profileForm.patchValue(formValues);
        this.originalFormValues = { ...formValues };
        this.profilePicturePreview = user.profilePictureUrl || '';
      }
    });

    this.profileService.getProfile().subscribe((response) => {
      if (response.payload) {
        this.notificationPrefs = response.payload.channelsMap;
      }
    });

    this.profileForm.valueChanges.subscribe(() => {
      this.updateZodErrors();
    });
  }

  private updateZodErrors(): void {
    this.zodErrors = {};
    Object.keys(this.profileForm.controls).forEach((key) => {
      const control = this.profileForm.get(key);
      if (control && control.invalid && control.touched) {
        if (control.errors?.['required']) {
          this.zodErrors[key] = `${this.getFieldDisplayName(key)} is required`;
        } else if (control.errors?.['invalidPhone']) {
          this.zodErrors[key] = 'Please enter a valid phone number';
        }
      }
    });

    // Check password confirmation
    const newPassword = this.profileForm.get('newPassword')?.value;
    const confirmPassword = this.profileForm.get('confirmPassword')?.value;

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      this.zodErrors['confirmPassword'] = 'Passwords do not match';
    }
  }

  private getFieldDisplayName(fieldName: string): string {
    const displayNames: Record<string, string> = {
      firstName: 'First Name',
      lastName: 'Last Name',
      phone: 'Phone Number',
      oldPassword: 'Old Password',
      newPassword: 'New Password',
      confirmPassword: 'Confirm Password',
    };
    return displayNames[fieldName] || fieldName;
  }

  saveChanges(): void {
    this.zodErrors = {};

    const formValue = this.profileForm.getRawValue();

    const zodResult = ProfileSchema.safeParse({
      firstName: formValue.firstName,
      lastName: formValue.lastName,
      phoneNumber: formValue.phone,
      profilePicture: formValue.profilePicture,
    });

    if (!zodResult.success) {
      zodResult.error.issues.forEach((err) => {
        if (err.path?.length) {
          const field = err.path[0] as string;
          this.zodErrors[field === 'phoneNumber' ? 'phone' : field] =
            err.message;
        }
      });
      Object.keys(this.profileForm.controls).forEach((key) => {
        this.profileForm.get(key)?.markAsTouched();
      });
      return;
    }

    this.isLoading = true;
    const payload = {
      fullName: `${formValue.firstName} ${formValue.lastName}`,
      phoneNumber: formValue.phone,
      profilePicture: formValue.profilePicture,
    };

    this.userService.updateUser(payload).subscribe({
      next: (response) => {
        this.isLoading = false;
        if (response.payload) {
          this.originalFormValues = {
            firstName: formValue.firstName,
            lastName: formValue.lastName,
            email: formValue.email,
            phone: formValue.phone,
            profilePicture: formValue.profilePicture,
          };
        }
        this.toastService.show(response.message, 'success');
      },
      error: (error) => {
        this.isLoading = false;
        this.toastService.show(error.message||error.error.message,'error');
      },
    });
  }

  changePassword(): void {
    const formValue = this.profileForm.getRawValue();

    if (!formValue.oldPassword) {
      this.zodErrors['oldPassword'] = 'Old password is required';
      this.profileForm.get('oldPassword')?.markAsTouched();
      return;
    }

    if (!formValue.newPassword) {
      this.zodErrors['newPassword'] = 'New password is required';
      this.profileForm.get('newPassword')?.markAsTouched();
      return;
    }

    if (!formValue.confirmPassword) {
      this.zodErrors['confirmPassword'] = 'Please confirm your new password';
      this.profileForm.get('confirmPassword')?.markAsTouched();
      return;
    }

    if (formValue.newPassword !== formValue.confirmPassword) {
      this.zodErrors['confirmPassword'] = 'Passwords do not match';
      this.profileForm.get('confirmPassword')?.markAsTouched();
      return;
    }

    const newPassword = formValue.newPassword;
    const passwordValid = /^(?=.*[A-Z])(?=.*\d).{8,}$/.test(newPassword);

    if (!passwordValid) {
      this.zodErrors['newPassword'] =
        'Password must be at least 8 characters long and include an uppercase letter and a number';
      this.profileForm.get('newPassword')?.markAsTouched();
      return;
    }

    this.zodErrors = {};

    this.isPasswordLoading = true;
    const payload = {
      oldPassword: formValue.oldPassword,
      newPassword: formValue.newPassword,
    };

    this.userService.changePassword(payload).subscribe({
      next: (response) => {
        if (response.payload) {
          this.profileForm.patchValue({
            oldPassword: '',
            newPassword: '',
            confirmPassword: '',
          });
        }
        this.toastService.show(response.message, 'success', 3000);
        this.isPasswordLoading = false;
        localStorage.removeItem('accessToken');
        this.router.navigate(['/auth']);
      },
      error: (error) => {
        this.toastService.show(error.error.message ||error.message, 'error');
        this.isPasswordLoading = false;
      },
    });
  }

  get hasPasswordChange(): boolean {
    const formValue = this.profileForm.getRawValue();
    return !!(
      formValue.oldPassword ||
      formValue.newPassword ||
      formValue.confirmPassword
    );
  }

  get hasProfileChange(): boolean {
    const formValue = this.profileForm.getRawValue();
    return !!(formValue.phone || formValue.lastName || formValue.firstName || formValue.profilePicture);
  }

  onImageSelected(image: string): void {
    this.profilePicturePreview = image;
  }

  onFileSelected(file: File | null): void {
    this.profileForm.patchValue({
      profilePicture: file,
    });
  }


  onNotificationChange(event: any, type: 'WHATSAPP' | 'SMS' | 'EMAIL'): void {
    this.notificationPrefs[type] = event.target.checked;
    this.profileService
      .updateNotificationPreferences(this.notificationPrefs)
      .subscribe({
        next: (response) => {
          if (response.payload) {
            this.notificationPrefs = response.payload.channelsMap;
          }
          this.toastService.show(response.message, 'success');
        },
        error: (error) => {
          this.toastService.show(error.message||error.error.message,'error');
        }
      });
  }

  onSuccessModalClose(): void {
    this.isSuccessModalVisible = false;
  }
}
